// frontend/src/features/citysense/housing-stock-modeller/components/HousingMap.tsx

import React, { useState } from 'react';
import { HousingPolygon } from '../types';
import { DEPRIVATION_LEVELS } from '../constants/housingConfig';
import { Plus, Minus } from 'lucide-react';

interface Props {
  polygons: HousingPolygon[];
  archetypeColors: Map<string, string>;
  selectedPolygonId: string | null;
  upgradedByOptimizer: Set<string>;
  viewMode: 'scenario' | 'deprivation' | 'optimizer' | 'reference';
  onPolygonClick: (id: string) => void;
}

export const HousingMap: React.FC<Props> = ({
  polygons,
  archetypeColors,
  selectedPolygonId,
  upgradedByOptimizer,
  viewMode,
  onPolygonClick,
}) => {
  const [zoom, setZoom] = useState(1);

  const getPolygonFill = (polygon: HousingPolygon) => {
    if (viewMode === 'deprivation') {
      return DEPRIVATION_LEVELS[polygon.deprivationIndex]?.fill || '#999';
    }
    return archetypeColors.get(polygon.archetype) || '#999';
  };

  const polygonCentroid = (pts: [number, number][]): [number, number] => {
    const x = pts.reduce((sum, [px]) => sum + px, 0) / pts.length;
    const y = pts.reduce((sum, [, py]) => sum + py, 0) / pts.length;
    return [x, y];
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.3, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.3, 0.5));
  const handleResetZoom = () => setZoom(1);

  // Calculate viewBox based on zoom with proper padding
  const baseSize = 2100;  // Increased from 2000
  const padding = 100;    // More padding
  const viewSize = baseSize / zoom;
  const viewOffset = -padding + (baseSize - viewSize) / 2;

  return (
    <div className="bg-white border-2 border-teal-200 rounded-xl shadow-lg overflow-hidden relative">
      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2 bg-white rounded-lg shadow-lg border-2 border-teal-200 overflow-hidden">
        <button
          onClick={handleZoomIn}
          className="p-2 hover:bg-teal-50 transition-colors border-b border-teal-200"
          title="Zoom In"
        >
          <Plus className="w-5 h-5 text-teal-700" />
        </button>
        <button
          onClick={handleResetZoom}
          className="px-2 py-1 text-xs font-bold text-teal-700 hover:bg-teal-50 transition-colors border-b border-teal-200"
          title="Reset Zoom"
        >
          1:1
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 hover:bg-teal-50 transition-colors"
          title="Zoom Out"
        >
          <Minus className="w-5 h-5 text-teal-700" />
        </button>
      </div>

      <svg 
        viewBox={`${viewOffset} ${viewOffset} ${viewSize} ${viewSize}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Polygons */}
        <g>
          {polygons.map((polygon) => {
            const isSelected = polygon.id === selectedPolygonId;
            const isUpgraded = upgradedByOptimizer.has(polygon.id);
            const fill = getPolygonFill(polygon);

            // In optimizer mode, dim non-upgraded polygons
            const opacity = viewMode === 'optimizer' && upgradedByOptimizer.size > 0
              ? (isUpgraded ? 0.95 : 0.3)
              : (isSelected ? 0.95 : 0.8);

            return (
              <g key={polygon.id}>
                <polygon
                  points={polygon.pts.map(([x, y]) => `${x},${y}`).join(' ')}
                  fill={fill}
                  stroke={isSelected ? '#0F766E' : isUpgraded ? '#F59E0B' : '#374151'}
                  strokeWidth={isSelected ? 6 : isUpgraded ? 4 : 1.5}
                  opacity={opacity}
                  className="cursor-pointer hover:opacity-100 transition-opacity"
                  onClick={() => onPolygonClick(polygon.id)}
                />
                
                {/* Star marker for upgraded polygons */}
                {isUpgraded && (
                  <text
                    x={polygonCentroid(polygon.pts)[0]}
                    y={polygonCentroid(polygon.pts)[1]}
                    fontSize="24"
                    fill="#F59E0B"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                    style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))' }}
                  >
                    ⭐
                  </text>
                )}
              </g>
            );
          })}
        </g>

        {/* Scale bar */}
        <g transform="translate(1700, 1900)">
          <rect x="0" y="0" width="200" height="8" fill="#fff" stroke="#374151" strokeWidth="2" />
          <text x="100" y="25" textAnchor="middle" fontSize="14" fill="#374151" fontWeight="bold">
            500m
          </text>
        </g>

        {/* North arrow */}
        <g transform="translate(1850, 100)">
          <path d="M 0,-30 L -15,30 L 0,20 L 15,30 Z" fill="#0F766E" stroke="#374151" strokeWidth="2" />
          <text x="0" y="50" textAnchor="middle" fontSize="16" fill="#374151" fontWeight="bold">
            N
          </text>
        </g>
      </svg>
    </div>
  );
};